import { useSyncExternalStore } from "react";
import {
  onModelStatus,
  sendModelCommand,
  UNKNOWN_MODEL,
  type ModelCommand,
  type ModelStatus,
} from "../src/model-link.js";
import type { ScrollMode } from "@cuekards/core/scroll-mode";
import { modelOffer } from "@cuekards/core/model-download/offer";
import type {
  ModelAction,
  ModelOffer,
} from "@cuekards/core/model-download/offer";
import { FOLLOW_CHECKING } from "./green-room.js";
import type { MessagePortLike } from "../src/protocol.js";

/**
 * The page's half of the first-visit download (web ticket 15).
 *
 * A module-level store rather than React state, for the reason `take-notice.ts`
 * is one: what it listens to is attached to a worker at startup, outside the
 * component tree, and two screens draw from it. Lifting it into a component would
 * make the subscription come and go with a mount, and the one thing that must not
 * happen is a download whose progress stops being read because somebody stepped
 * into the settings.
 *
 * **Every command goes out from here rather than from the worker's own timer**,
 * and that is what makes web ticket 15's persistence rule structural. Persistent
 * storage has to be asked for before anything is cached; `StorageManager.persist`
 * is a window API, so the worker cannot ask; therefore the thing that says
 * "start" has to be the thing that can ask, which is the page. There is no
 * ordering to remember, because there is no other order available.
 */

export interface ModelDownloadPorts {
  /** The recognition worker. Status arrives on it, commands go out on it. */
  port: MessagePortLike;
  /**
   * Ask the browser to keep what is about to be written (web ticket 13's
   * `askThisBrowserToPersist`).
   *
   * Awaited before `start` and not before anything else. Its answer is not
   * checked: a refusal means the cache may be evicted under disk pressure, which
   * is a thing to know and not a reason to refuse somebody a download — and
   * Chromium decides from site engagement, so the first visit is exactly the one
   * likely to be told no.
   */
  persist: () => Promise<unknown>;
}

let status: ModelStatus = UNKNOWN_MODEL;
const listeners = new Set<() => void>();
let send: ((command: ModelCommand) => void) | null = null;

/**
 * Listen to the recognition worker and give the page a way to answer back.
 *
 * Called once, from `main.tsx`, with the worker it has just constructed. Returns
 * the detach, which is what the tests use and what a page would use if it ever
 * tore a worker down — after which commands are dropped rather than posted at a
 * port with nothing on the other end.
 */
export function attachModelDownload(ports: ModelDownloadPorts): () => void {
  publish(UNKNOWN_MODEL);
  const off = onModelStatus(ports.port, publish);
  send = (command) => {
    if (command !== "start") {
      sendModelCommand(ports.port, command);
      return;
    }
    void ports
      .persist()
      // A browser with no `StorageManager`, or one that threw discussing it. The
      // download is not the thing to cancel over it.
      .catch(() => undefined)
      .then(() => sendModelCommand(ports.port, "start"));
  };
  return () => {
    off();
    send = null;
  };
}

/**
 * There is no recognition worker at all, and why.
 *
 * The one thing the page knows that the worker cannot report: its own script was
 * not there to run. Applied only over the state the page starts in, so a worker
 * that did start and is merely slow wins over a page that gave up on it.
 */
export function modelUnavailable(why: string): void {
  if (status.phase !== "checking") return;
  publish({ phase: "unavailable", why });
}

export function modelCommand(command: ModelCommand): void {
  send?.(command);
}

export function currentModelStatus(): ModelStatus {
  return status;
}

export function subscribeToModelStatus(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useModelStatus(): ModelStatus {
  return useSyncExternalStore(subscribeToModelStatus, currentModelStatus);
}

function publish(next: ModelStatus): void {
  status = next;
  for (const listener of listeners) listener();
}

/**
 * What is on screen about the model, and the button that changes it.
 *
 * Both types and the function are `@cuekards/core`'s, and moved there with the
 * loader's third reader (mobile-app ticket 06): the phone draws the same seven
 * phases out of the same machine, and a second opinion about what `downloading`
 * says would be exactly the drift this function was written to prevent — across
 * two products now rather than two components. Re-exported because every reader
 * of them on this host — the download panel, the prompter's chrome, the dialog
 * below — meets them as this host's own vocabulary.
 */
export type { ModelAction, ModelOffer };
export { modelOffer };

/* ---- Follow my voice: the box in the green room, and what it asks (flow
 * ticket 06) ---- */

/** What the box is doing, for the state the room is in. */
export interface FollowBox {
  /** Ticked. */
  on: boolean;
  /** Nothing a visitor does would turn it on — and it is not already on. Those
   * are two conditions rather than one; see {@link followBox}. */
  disabled: boolean;
  /** Why it cannot be had here, whether or not that leaves it pressable. */
  reason?: string;
  /** Whether ticking it opens the dialog rather than simply turning it on. */
  asks: boolean;
}

/**
 * The box, from the live mode and what the browser has of the model.
 *
 * **Ticked by the mode, not by the stored choice.** The setting is what a tab
 * *opens* on; what the box reports is what the prompter is doing now, so a cycle
 * away from `speech` in the prompter's `⋯` menu unticks it. The alternative —
 * drawing it from `Settings.followVoice` — puts a ticked box over a prompter in
 * manual mode, which is the one thing a switch may never do. Nothing is lost:
 * the stored choice is still what the next visit opens on, the same two-layer
 * arrangement the font size is on.
 *
 * **Only `unavailable` refuses the press.** `checking` deliberately does not, and
 * that is what keeps the box from flickering from dead to live as the manifest
 * lands — a control that changes under a hand on the way to it is worse than one
 * that takes a beat to answer, and answering is {@link followModal}'s job.
 *
 * **And `unavailable` refuses it only while it is off**, which is the case that
 * is easy to miss and is the one that matters. A browser with `followVoice`
 * stored opens in `speech`, and if the recogniser then cannot be had — a
 * deployment serving no model, a worker script that did not load — the box is
 * ticked over a prompter that will never move. Dead *and* ticked is a trap: the
 * `⋯` cycle persists nothing by design, so every reload comes back to it and
 * there is no way out of a mode this deployment cannot deliver. Whatever else is
 * true, the switch that turned speech-following on has to be able to turn it off
 * again (goal G5). Unticking it there is a normal untick — `constant`, remembered
 * — and the stored choice is left alone until the visitor makes one, so a
 * transient worker failure does not quietly wipe a decision they made.
 */
export function followBox(input: {
  status: ModelStatus;
  mode: ScrollMode;
}): FollowBox {
  const { status, mode } = input;
  const on = mode === "speech";
  if (status.phase === "unavailable")
    return {
      on,
      disabled: !on,
      // The panel's sentence rather than a second one: it already names the
      // cause and says the prompter still works without it (goal G5).
      reason: modelOffer(status)?.says,
      asks: false,
    };
  return {
    on,
    disabled: false,
    // The two phases a download can be started from, plus the one that does not
    // yet know which of them it is.
    asks:
      status.phase === "needed" ||
      status.phase === "failed" ||
      status.phase === "checking",
  };
}

/** What the dialog says, and what pressing its affirmative would do. */
export interface FollowModal {
  says: string;
  /** The affirmative, where there is something for it to start. Absent means the
   * dialog is still waiting to find out, and the button is dead. */
  confirm?: ModelAction;
}

/**
 * The dialog behind the box, or `null` where there is nothing left to ask.
 *
 * **The sentence about the cost is {@link modelOffer}'s and is not rewritten
 * here.** It quotes `status.bytes`, which on `needed` is what is still *absent*
 * rather than the manifest's total — a download resumes at file granularity
 * (ADR-0016) — so a figure written into this copy would be wrong by whatever is
 * already cached, and wrong differently on every resumed download. Reading the
 * one function is also what keeps the panel below the box and the dialog in front
 * of it saying the same thing, which is the arrangement `PRIVACY_CLAIMS` and
 * `WALKTHROUGH_STEPS` are already on.
 *
 * **`confirm` is absent unless the live phase would accept a `start`.**
 * `startModelLoader` takes one only from `needed` and `failed`; from anywhere
 * else it is a silent no-op, and a dialog whose affirmative quietly does nothing
 * is the failure mode worth designing out rather than testing for. `checking` is
 * the case that makes this real — the dialog opens on it, says so, and its button
 * comes alive when the manifest lands.
 *
 * `null` is "there is nothing to agree to": the model is there, or already on its
 * way, or will never be. The caller closes the dialog and turns the box on.
 */
export function followModal(status: ModelStatus): FollowModal | null {
  if (status.phase === "checking") return { says: FOLLOW_CHECKING };
  if (status.phase !== "needed" && status.phase !== "failed") return null;
  const offer = modelOffer(status);
  return offer === null
    ? null
    : {
        says: offer.says,
        ...(offer.action === undefined ? {} : { confirm: offer.action }),
      };
}

